import React, { useState, useEffect } from 'react';
import { Col, Row, Skeleton } from 'antd';
import { IForm, IGetPopupContainer, ILead } from '@shared-components/models';
import Form, { RuleObject } from 'antd/lib/form';
import {
  ASSIGNEE_PLACEHOLDER,
  ASSIGNEE_LABEL,
  EMAIL_LABEL,
  EMAIL_PLACEHOLDER,
  FIRST_NAME_LABEL,
  FIRST_NAME_PLACEHOLDER,
  LAST_NAME_LABEL,
  LAST_NAME_PLACEHOLDER,
  PHONE_LABEL,
  regex,
  GUTTER,
  LEAD_INITIAL_DATA,
  AUTH_MESSAGE,
} from '@moxie/constants';
import { Input } from 'libs/shared/src/shared';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import moment, { Moment } from 'moment';
import { useAppSelector } from '@crm/core';
import { checkLeadEmail } from '@crm/services.api';
import { UserSelectBox } from '../../lead/component/user-select-box';
import { useFetchAssignableUsers } from '@crm/libs/hooks/contacts';

const LeadForm: React.FC<IForm<ILead>> = ({
  form,
  onSubmit,
  isView,
  initialData,
  setDisabled,
}: IForm<ILead>) => {
  const [phone, setPhone] = useState('');
  const [country_code, setCountryCode] = useState<string | any>('');
  const { loading } = useAppSelector((store) => ({
    loading: store.users.loading,
  }));

  const { users } = useFetchAssignableUsers(initialData?.id);

  const onFieldsChange = (allFields: string | any[]) => {
    if (allFields.length > 0) {
      for (const field of allFields) {
        field.errors.length < 1 ? setDisabled(false) : setDisabled(true);
      }
    }
  };

  useEffect(() => {
    if (initialData) {
      initialData.date_of_birth = changeDateToMoment(
        initialData?.date_of_birth
      );
      initialData.date_of_birth = changeDateToMoment(initialData.date_of_birth);
      initialData.visa_expiry_date = changeDateToMoment(
        initialData.visa_expiry_date
      );
      initialData.passport_expiry_date = changeDateToMoment(
        initialData.passport_expiry_date
      );
      setCountryCode(initialData?.country_code);
      form.setFieldsValue(initialData);
      if (initialData?.crm_assignee_id === null) {
        form.setFieldsValue({
          assignee_user_id: '',
        });
      } else {
        form.setFieldsValue({
          assignee_user_id: initialData?.crm_assignee_id,
        });
      }
    } else {
      form.setFieldsValue(LEAD_INITIAL_DATA);
      form.setFieldsValue({
        assignee_user_id: '',
      });
    }
  }, [form, initialData, isView]);

  const changeDateToMoment = (date?: string | Moment): Moment | undefined => {
    if (date) return moment(date);
    return;
  };

  const handleSubmit = async (data: ILead) => {
    data.country_code = country_code;
    data.email = data?.email?.replace(/ /g, '');
    data.phone = data?.phone !== 'undefined undefined' && data?.phone ?  data?.phone?.substring(data?.country_code?.length - 1) : '';
    onSubmit && onSubmit(data);
  };

  const handleCountryCodeChange = (input: any, country: any) => {
    if(input){
      setPhone(input?.slice(country?.dialCode?.length));
      setCountryCode(`+${country?.dialCode}`);
    } else  {
      setPhone('');
      setCountryCode('');
    }
    setDisabled(false);
  };

  const debounce = (func: any, delay: number) => {
    let timer: any;
    return (...args: any) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  const handleEmailChange = debounce(async (event: any): Promise<any> => {
    let value = event.target?.value;
    if (initialData?.email === value) return true;
    value = value?.toLowerCase();
    if (value.length === 0) {
      form.setFields([
        {
          name: 'email',
          errors: [AUTH_MESSAGE.INVALID_EMAIL],
        },
      ]);
      return Promise.resolve(true);
    } else {
      const trimValue = value.replace(/ /g, '');

      if (String(trimValue).match(new RegExp(regex.EMAIL))) {
        const result = await validateEmail({ email: trimValue });
        if (result) {
          return Promise.resolve(true);
        } else {
          form.setFields([
            {
              name: 'email',
              errors: [AUTH_MESSAGE.REGISTER_EMAIL_TAKEN],
              validating: false,
            },
          ]);

          setDisabled(true);
          return;
        }
      } else {
        form.setFields([
          {
            name: 'email',
            errors: [AUTH_MESSAGE.INVALID_EMAIL],
          },
        ]);
      }
      setDisabled(false);
    }
  }, 500);

  const validateEmail = async (value: { email: string }) => {
    try {
      const res = await checkLeadEmail(value?.email);
      return !!res?.data?.success;
    } catch (err) {
      return false;
    }
  };

  const validatePhoneNumber = (_: RuleObject, value: string) => {
    if (value && value !== 'undefined undefined' && value.replace(/\D/g, '').length < 10) {
      setDisabled(true);
      return Promise.reject('Please enter a valid phone number');
    }
    return Promise.resolve();
  };

  return (
    <>
      {loading && initialData?.id ? (
        <Skeleton active />
      ) : (
        <Form
          layout="vertical"
          onFinish={handleSubmit}
          form={form}
          scrollToFirstError={true}
          onFieldsChange={onFieldsChange}
        >
          <Row gutter={GUTTER}>
            <Col span={12}>
              <Form.Item
                name="first_name"
                label={FIRST_NAME_LABEL}
                validateFirst={true}
                rules={[
                  { required: true },
                  {
                    pattern: new RegExp(regex.ONLY_STRING),
                    message: AUTH_MESSAGE.INVALID_FIRST_NAME,
                  },
                  { max: 50 },
                ]}
              >
                <Input
                  data-testid="first_name"
                  className="note-title-input"
                  placeholder={FIRST_NAME_PLACEHOLDER}
                  disabled={isView}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="last_name"
                label={LAST_NAME_LABEL}
                validateFirst={true}
                rules={[
                  { required: true },
                  {
                    pattern: new RegExp(regex.ONLY_STRING),
                    message: AUTH_MESSAGE.INVALID_LAST_NAME,
                  },
                  { max: 50 },
                ]}
              >
                <Input
                  data-testid="last_name"
                  className="note-title-input"
                  placeholder={LAST_NAME_PLACEHOLDER}
                  disabled={isView}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={GUTTER}>
            <Col span={12}>
              <Form.Item
                name="email"
                label={EMAIL_LABEL}
                validateFirst={true}
                rules={[
                  {
                    required: true,
                    message: AUTH_MESSAGE.INVALID_EMAIL,
                  },
                  {
                    pattern: new RegExp(regex.EMAIL),
                    message: AUTH_MESSAGE.INVALID_EMAIL,
                  },
                ]}
              >
                <Input
                  data-testid="email"
                  className="note-title-input"
                  placeholder={EMAIL_PLACEHOLDER}
                  disabled={isView}
                  onChangeCapture={handleEmailChange}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label={PHONE_LABEL}
                data-testid="phone"
                className="country_dial_code"
                rules={[
                  {
                    validator: validatePhoneNumber,
                  },
                ]}
              >
                <PhoneInput
                  value={phone}
                  placeholder={''}
                  onChange={handleCountryCodeChange}
                  enableLongNumbers={15}
                  autocompleteSearch={false}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={GUTTER}>
            <Col span={24}>
              <div id="lead_assignee_id" className="relative">
                <Form.Item
                  name="assignee_user_id"
                  data-testid="assignee_user_id"
                  label={ASSIGNEE_LABEL}
                  required={true}
                >
                  <UserSelectBox
                    className="note-title-input"
                    getPopupContainer={(): IGetPopupContainer =>
                      document.getElementById('lead_assignee_id')
                    }
                    placeholder={!isView ? ASSIGNEE_PLACEHOLDER : '-'}
                    disabled={isView}
                    users={users}
                    showUnassigned={false}
                    allowClear={false}
                  />
                </Form.Item>
              </div>
            </Col>
          </Row>
        </Form>
      )}
    </>
  );
};

export { LeadForm };
